import {
  useGetLatestArticlesByCategoryQuery,
  type GetLatestArticlesByCategoryQuery,
  Locale,
} from '@seek/cmsu-cms-connect';
import { LatestArticlesByCategory } from '@seek/cmsu-components';
import { CMSUProvider } from '@seek/cmsu-components/src/hooks/useCMSUContext';
import React from 'react';
import { useConfig } from 'src/client/shared/hooks';
import { sectionMap } from 'src/loaders';

type Props = {
  categoryName: string;
  categorySlug: string;
};

type LatestArticles = GetLatestArticlesByCategoryQuery['latestArticles'];

const getHAMISection: (section: string) => string = (section) =>
  sectionMap[section] || section;

export const filterCategoryArticles = (
  data: GetLatestArticlesByCategoryQuery,
  hygraphLocale: Locale,
): LatestArticles => {
  const articles = data.latestArticles.map((article) => {
    const { featureImage } = article;
    if (featureImage.length > 0) {
      const filteredFeatureImage = featureImage.find(
        (img) => img.locale === hygraphLocale,
      );
      const defaultFeatureImage = featureImage.find(
        (img) => img.locale === Locale.Default,
      );
      return {
        ...article,
        featureImage: filteredFeatureImage
          ? [filteredFeatureImage]
          : [defaultFeatureImage],
      };
    }
    return {
      ...article,
      featureImage: [],
    };
  });
  return articles as LatestArticles;
};

export const LatestArticlesByCategoryLoader = ({
  categoryName,
  categorySlug,
}: Props) => {
  const { site, hygraphSite, section, hygraphLocale, language } = useConfig();

  const locales =
    hygraphLocale === Locale.IdId || hygraphLocale === Locale.ThTh
      ? [hygraphLocale]
      : [hygraphLocale, Locale.Default];

  // if site is id_id or th_th, don't request articles for locales
  // some articles should be excluded if their title is dash
  const { data } = useGetLatestArticlesByCategoryQuery({
    variables: {
      where: {
        title_not: '-',
        category: {
          slug: categorySlug,
          sites_some: { name: hygraphSite },
          relatedCategorySection: {
            sectionName: getHAMISection(section),
          },
        },
        sites_some: { name: hygraphSite },
      },
      locale: locales,
      localesWithDefault: [hygraphLocale, Locale.Default],
    },
  });

  if (!data?.latestArticles.length) {
    return null;
  }

  const latestArticles = filterCategoryArticles(data, hygraphLocale);

  return (
    <CMSUProvider config={{ language }}>
      <LatestArticlesByCategory
        latestArticles={latestArticles}
        categoryName={categoryName}
        categorySlug={categorySlug}
        section={section}
        site={site}
      />
    </CMSUProvider>
  );
};
